import * as React from "react"
import { Search } from "lucide-react"
import { EMOJI_CATEGORIES, searchEmojis } from "@/data/emojis"

interface EmojiPickerProps {
  onSelect: (emoji: string) => void
  onClose: () => void
  maxHeight?: number
}

export function EmojiPickerContent({ onSelect, onClose, maxHeight }: EmojiPickerProps) {
  const [search, setSearch] = React.useState("")
  const [activeCategory, setActiveCategory] = React.useState("frequent")
  const gridRef = React.useRef<HTMLDivElement>(null)
  const categoryRefs = React.useRef<Record<string, HTMLDivElement | null>>({})
  void onClose

  const clampedMaxHeight = typeof maxHeight === "number" && Number.isFinite(maxHeight)
    ? Math.max(220, Math.floor(maxHeight))
    : null
  const gridHeight = clampedMaxHeight
    ? Math.max(120, Math.min(260, clampedMaxHeight - 104))
    : 260

  const searchResults = React.useMemo(() => {
    if (!search.trim()) return null
    return searchEmojis(search)
  }, [search])

  const handleCategoryClick = (categoryId: string) => {
    setSearch("")
    setActiveCategory(categoryId)
    const el = categoryRefs.current[categoryId]
    if (el && gridRef.current) {
      gridRef.current.scrollTo({
        top: el.offsetTop - gridRef.current.offsetTop,
        behavior: "smooth",
      })
    }
  }

  const handleEmojiClick = (emoji: string) => {
    onSelect(emoji)
  }

  return (
    <div
      className="w-[320px] md:w-[352px] bg-[#1C1C32] border-3 border-[#2A2A45] rounded-2xl shadow-[6px_6px_0_0_#1a1a1a] overflow-hidden"
      style={clampedMaxHeight ? { maxHeight: clampedMaxHeight } : undefined}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Search */}
      <div className="p-2.5 border-b-2 border-[#2A2A45]/40">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6A6A8A]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search emojis..."
            className="w-full pl-8 pr-3 py-2 text-sm font-medium rounded-xl border-2 border-[#2A2A45]/50 bg-[#1A1A30] focus:outline-none focus:ring-2 focus:ring-[#F37020] focus:border-transparent"
            autoFocus
          />
        </div>
      </div>

      {/* Category tabs */}
      <div className="flex gap-0.5 px-2 py-1.5 border-b-2 border-[#2A2A45]/40 overflow-x-auto scrollbar-hide">
        {EMOJI_CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            onClick={() => handleCategoryClick(cat.id)}
            className={`flex-shrink-0 w-8 h-8 rounded-lg text-base flex items-center justify-center transition-colors ${
              activeCategory === cat.id && !search
                ? "bg-[#F37020]/15 border border-[#F37020]/30"
                : "hover:bg-[#1A1A30]"
            }`}
            title={cat.name}
          >
            {cat.icon}
          </button>
        ))}
      </div>

      {/* Emoji grid */}
      <div
        ref={gridRef}
        className="overflow-y-auto p-2"
        style={{ height: gridHeight }}
        onScroll={() => {
          if (search || !gridRef.current) return
          // Update active category based on scroll position
          const scrollTop = gridRef.current.scrollTop + gridRef.current.offsetTop
          let current = EMOJI_CATEGORIES[0]!.id
          for (const cat of EMOJI_CATEGORIES) {
            const el = categoryRefs.current[cat.id]
            if (el && el.offsetTop <= scrollTop + 40) {
              current = cat.id
            }
          }
          setActiveCategory(current)
        }}
      >
        {searchResults ? (
          searchResults.length > 0 ? (
            <div>
              <p className="text-[10px] uppercase tracking-wide font-black text-[#6A6A8A] px-1 mb-1.5">
                Search Results
              </p>
              <div className="grid grid-cols-8 gap-0.5">
                {searchResults.map((emoji, i) => (
                  <button
                    key={`search-${i}`}
                    onClick={() => handleEmojiClick(emoji)}
                    className="w-9 h-9 rounded-lg text-xl flex items-center justify-center hover:bg-[#F37020]/10 hover:scale-110 transition-all"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-sm text-[#6A6A8A] font-medium">
              No emojis found
            </div>
          )
        ) : (
          EMOJI_CATEGORIES.map((cat) => (
            <div
              key={cat.id}
              ref={(el) => {
                categoryRefs.current[cat.id] = el
              }}
            >
              <p className="text-[10px] uppercase tracking-wide font-black text-[#6A6A8A] px-1 mb-1.5 mt-2 first:mt-0">
                {cat.name}
              </p>
              <div className="grid grid-cols-8 gap-0.5">
                {cat.emojis.map((emoji, i) => (
                  <button
                    key={`${cat.id}-${i}`}
                    onClick={() => handleEmojiClick(emoji)}
                    className="w-9 h-9 rounded-lg text-xl flex items-center justify-center hover:bg-[#F37020]/10 hover:scale-110 transition-all"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
